import { useEffect } from "react";
import type {
  PlanDetailResponse,
  PlanResponse,
  PlanTypeContraint,
} from "../../state/api.types";
import { api } from "../../state/api";
import { useDebounce } from "../../utilities/useDebounce";
import { useController, useForm, type Control } from "react-hook-form";

export function usePlanConstraintByFieldType(
  plan: PlanDetailResponse,
): Record<string, PlanTypeContraint | undefined> {
  if (!plan.plan_type) return {};
  return Object.fromEntries(
    plan.plan_type.constraints.map((constraint) => [
      constraint.field,
      constraint,
    ]),
  );
}
export type UsageKwh = 500 | 1000 | 2000;
export const USAGE_KWH_ITEMS = [
  { id: 500, label: "500 kWh" },
  { id: 1000, label: "1,000 kWh" },
  { id: 2000, label: "2,000 kWh" },
] as const satisfies { id: UsageKwh; label: string }[];

export type TermMonths = "12" | "24" | "36";
export const TERM_MONTHS_ITEMS = [
  {
    id: "12",
    label: "12 Months",
  },
  {
    id: "24",
    label: "24 Months",
  },
  {
    id: "36",
    label: "36 Months",
  },
];

export type FormValues = {
  name: string;
  description?: string | null;
  termMonths: TermMonths;
  baseFee: string;
  monthlyCommission: string;
  exportRate: string;
  renewalPlanUuid: string | null;
};

export function toTermMonths(value: string): TermMonths {
  const item = TERM_MONTHS_ITEMS.find((item) => item.id === value);
  if (item) {
    return item.id as TermMonths;
  }
  throw new Error(`Invalid termMonth value ${value}`);
}

export type ServiceTerritory =
  | "ONCOR"
  | "CENTERPOINT"
  | "CenterPoint"
  | "AEP_CENTRAL"
  | "AEP_NORTH"
  | "TNMP"
  | "LUBBOCK";

export const SERVICE_TERRITORY_ITEMS = [
  {
    id: "ONCOR",
    label: "ONCOR",
  },
  {
    id: "CENTERPOINT",
    label: "CenterPoint",
  },
  {
    id: "AEP_CENTRAL",
    label: "AEP Central",
  },
  {
    id: "AEP_NORTH",
    label: "AEP North",
  },
  {
    id: "TNMP",
    label: "TNMP",
  },
  {
    id: "LUBBOCK",
    label: "Lubbock P&L",
  },
];

export function toServiceTerritory(value: string): ServiceTerritory {
  const item = SERVICE_TERRITORY_ITEMS.find((item) => item.id === value);
  if (item) {
    return item.id as ServiceTerritory;
  }
  throw new Error(`Invalid serviceTerritory value ${value}`);
}

export function useFormValues(control: Control<FormValues>) {
  return {
    name: useController({ control, name: "name" }),
    description: useController({ control, name: "description" }),
    termMonths: useController({ control, name: "termMonths" }),
    baseFee: useController({ control, name: "baseFee" }),
    monthlyCommission: useController({ control, name: "monthlyCommission" }),
    exportRate: useController({ control, name: "exportRate" }),
    renewalPlanUuid: useController({ control, name: "renewalPlanUuid" }),
  };
}

export function useUpdatedPlanComparison(
  plan: PlanResponse,
  serviceTerritory: ServiceTerritory,
  termMonths: TermMonths,
  control: Control<FormValues>,
) {
  const [comparePlan, comparePlanState] = api.useComparePlanMutation();
  const { exportRate, baseFee, monthlyCommission } = useFormValues(control);
  const comparePlanRequest = {
    planUUID: plan.uuid,
    export_rate_kwh_dollars: (
      parseFloat(exportRate.field.value) / 100 || 0
    ).toString(),
    early_termination_fee_dollars: plan.early_termination_fee_dollars,
    plan_monthly_cost_dollars: (
      parseFloat(baseFee.field.value) || 0
    ).toString(),
    monthly_commission_dollars: (
      parseFloat(monthlyCommission.field.value) || 0
    ).toString(),
  };
  const hasNaN = [
    comparePlanRequest.export_rate_kwh_dollars,
    comparePlanRequest.plan_monthly_cost_dollars,
    comparePlanRequest.monthly_commission_dollars,
  ].includes("NaN");
  const hasInvalid =
    exportRate.fieldState.invalid ||
    baseFee.fieldState.invalid ||
    monthlyCommission.fieldState.invalid;
  const validComparePlanRequest =
    hasNaN || hasInvalid ? undefined : comparePlanRequest;
  const debouncedComparePlanRequest = useDebounce(validComparePlanRequest, 500);
  useEffect(() => {
    if (!debouncedComparePlanRequest) {
      return;
    }
    comparePlan({
      ...debouncedComparePlanRequest,
      term_months: parseInt(termMonths),
      service_territory: serviceTerritory,
    });
  }, [debouncedComparePlanRequest, comparePlan, termMonths, serviceTerritory]);
  return comparePlanState;
}

export function useEditPlanForm(plan: PlanResponse) {
  return useForm<FormValues>({
    defaultValues: {
      name: plan.name,
      description: plan.description,
      termMonths: toTermMonths(plan.term_months.toString()),
      baseFee: plan.plan_monthly_cost_dollars ?? "0.00",
      monthlyCommission: plan.partner_commission_per_month_dollars ?? "0.00",
      exportRate:
        (plan.export_rate_kwh_dollars &&
          (parseFloat(plan.export_rate_kwh_dollars) * 100).toString()) ??
        undefined,
      renewalPlanUuid: plan.renewal_plan_uuid,
    },
    mode: "onChange",
  });
}
